/**
 * "Continue on your computer" — the honest exit from a phone.
 *
 * Two things the browser on a phone genuinely cannot do: run the 6-stem engine
 * (143MB of weights whose inference peak is past what a mobile tab is given),
 * and capture a tab's audio (no API on iOS, no tab audio on Android). Both were
 * previously dead ends — the phone quietly did something smaller, or a card
 * said "use a computer" and left the user to remember the link themselves.
 *
 * This is the bridge. The share sheet is the point: it is the one surface that
 * already knows how to reach the user's own machine — AirDrop, Messages, Mail,
 * whatever they use — with no address asked for and nothing stored by us.
 * Copy is the fallback where `navigator.share` is missing.
 */
import { useEffect, useRef, useState } from 'react';
import { sendLink, copyLink, type DesktopHandoff, desktopUrl } from '../lib/deviceTier';
import './continueOnDesktop.css';

export interface ContinueOnDesktopProps {
  /** What the desktop should pick up: a bare file hand-off or a song link. */
  handoff: DesktopHandoff;
  /** Heading. Says what they GET over there, not what this device lacks. */
  title: string;
  /** One sentence of why. Kept to one — this is a detour, not a pitch. */
  body: string;
  /** Text for the share sheet itself (a Message/Mail body, not UI copy). */
  shareText: string;
  /** Analytics hook — the caller owns which funnel this belongs to. */
  onSent?: (how: 'shared' | 'copied' | 'dismissed' | 'failed') => void;
  /**
   * Fired ONCE, the first time the card is actually on screen — not when it
   * mounts. The result card sits under a full-width CTA on a phone, so a
   * mount-time impression would count people who never scrolled to it and
   * turn "did they see the way out?" into a number that is always 100%.
   * Without this, `onSent` has no denominator: a card nobody scrolls to and a
   * card everybody ignores look identical.
   */
  onShown?: () => void;
  /** Optional slot under the buttons (e.g. "or upload a song instead"). */
  children?: React.ReactNode;
  /**
   * `card` is the full panel — for the moment the user actually wants what the
   * phone can't do. `inline` is one sentence and one text button, for stating
   * the limit up front without out-shouting the thing the page is asking for.
   */
  variant?: 'card' | 'inline';
}

export function ContinueOnDesktop({
  handoff, title, body, shareText, onSent, onShown, children, variant = 'card',
}: ContinueOnDesktopProps) {
  const url = desktopUrl(handoff);
  const [state, setState] = useState<'idle' | 'sent' | 'copied' | 'failed'>('idle');
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  // ── impression ────────────────────────────────────────────────────────
  // Half the card, for a continuous 400ms. Half because the inline variant is
  // two lines and a threshold near 1 would miss it behind a keyboard or a
  // sticky bar; the dwell because a phone flings past a card on the way down
  // and a frame of it crossing the viewport is not a thing anyone read.
  const rootRef = useRef<HTMLElement | null>(null);
  // Held in a ref, not state: firing must not re-render, and the guard has to
  // survive the effect re-running without resetting the "already counted" bit.
  const shownRef = useRef(false);
  // The callback identity changes every render (callers pass an arrow), so it
  // lives in a ref and stays out of the effect's deps — otherwise the observer
  // would be torn down and rebuilt on every parent render.
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;

  useEffect(() => {
    if (shownRef.current) return;
    const fire = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      try { onShownRef.current?.(); } catch { /* telemetry never breaks the card */ }
    };
    const el = rootRef.current;
    // No element yet, or a browser without the observer: count it as shown.
    // Over-counting on an old browser beats a silent hole in the denominator.
    if (!el || typeof IntersectionObserver === 'undefined') { fire(); return; }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          if (timer === undefined) timer = setTimeout(() => { fire(); io.disconnect(); }, 400);
        } else if (timer !== undefined) {
          // Scrolled back off before the dwell elapsed — that was a fling past,
          // not a read. Reset so the next arrival has to earn it again.
          clearTimeout(timer);
          timer = undefined;
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => { if (timer !== undefined) clearTimeout(timer); io.disconnect(); };
  }, []);

  async function primary() {
    const how = await sendLink(url, 'Riffloop', shareText);
    onSent?.(how);
    // A dismissed share sheet leaves the button alone: they closed it on
    // purpose, and "Sent!" would be a lie about something they just cancelled.
    if (how === 'shared') setState('sent');
    else if (how === 'copied') setState('copied');
    else if (how === 'failed') setState('failed');
  }

  async function secondary() {
    const ok = await copyLink(url);
    onSent?.(ok ? 'copied' : 'failed');
    setState(ok ? 'copied' : 'failed');
  }

  const label = state === 'sent' ? 'Link sent ✓'
    : state === 'copied' ? 'Link copied ✓'
    : canShare ? 'Send the link →' : 'Copy the link';

  if (variant === 'inline') {
    return (
      <p className="cod-inline" ref={rootRef as React.RefObject<HTMLParagraphElement>}>
        {body}{' '}
        <button className="cod-inline-b" onClick={() => void primary()}>{label}</button>
      </p>
    );
  }

  return (
    <section className="cod" ref={rootRef as React.RefObject<HTMLElement>}>
      <div className="cod-head">
        <span className="cod-ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
          </svg>
        </span>
        <div className="cod-t">{title}</div>
      </div>
      <p className="cod-s">{body}</p>
      <div className="cod-row">
        <button className="cod-btn" onClick={() => void primary()}>{label}</button>
        {/* Only offered when the primary button is the share sheet — otherwise
            it is the same action twice under two names. */}
        {canShare && (
          <button className="cod-alt" onClick={() => void secondary()}>
            {state === 'copied' ? 'Copied' : 'Copy link'}
          </button>
        )}
      </div>
      {/* The link itself, so nobody has to trust a button with where it points.
          Wraps rather than scrolls — a phone has nowhere to scroll it. */}
      <div className="cod-url" title={url}>{url.replace(/^https?:\/\//, '')}</div>
      {state === 'failed' && (
        <p className="cod-err">Couldn’t open the share sheet. Long-press the link above to copy it.</p>
      )}
      {children}
    </section>
  );
}
